/**
 * A agenda do dia — a tela que substitui o caderno.
 *
 * Tudo que importa cabe aqui: o dia inteiro em uma linha do tempo, a navegação
 * entre dias por deslize de polegar e o botão de novo agendamento embaixo, onde
 * a mão já está.
 */

import type { AppointmentView, DayAgendaView, SessionContext } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ScreenHeader } from '../../App.tsx';
import { INACTIVE, Timeline } from '../../components/Timeline.tsx';
import { Button, EmptyState, Spinner } from '../../components/ui.tsx';
import { api, subscribeToAgenda } from '../../lib/api.ts';
import { useSession, useTenant } from '../../lib/contexts.tsx';
import { addDays, longDateOf, relativeDay, todayIn } from '../../lib/format.ts';
import { AgendaOverview, type AgendaScope } from './AgendaOverview.tsx';
import { AppointmentSheet } from './AppointmentSheet.tsx';
import { NewAppointmentSheet } from './NewAppointmentSheet.tsx';

interface StaffMember {
  id: string;
  name: string;
  role: string;
}

export function Agenda() {
  const { tenant } = useTenant();
  const session = useSession();
  const queryClient = useQueryClient();

  const today = todayIn(tenant.timezone);
  const [date, setDate] = useState(today);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [selected, setSelected] = useState<AppointmentView | null>(null);
  const [creatingAt, setCreatingAt] = useState<number | null>(null);
  const [scope, setScope] = useState<AgendaScope>('dia');

  const isOwner = session?.membership?.role === 'OWNER';

  // As setas andam na unidade da visão: um dia, uma semana, um mês.
  const step = scope === 'dia' ? 1 : scope === 'semana' ? 7 : 30;

  const { data: staffData } = useQuery({
    queryKey: ['staff'],
    queryFn: () => api.get<{ staff: StaffMember[] }>('/schedule/staff'),
    enabled: isOwner,
  });

  const { data: agenda, isPending } = useQuery({
    queryKey: ['agenda', date, staffId],
    queryFn: () =>
      api.get<DayAgendaView>('/appointments/agenda', { date, membershipId: staffId ?? undefined }),
  });

  // Alguém pediu horário ou cancelou enquanto a tela está aberta: recarrega
  // sozinho. O barbeiro está com a tesoura na mão, não vai puxar para atualizar.
  useEffect(() => {
    return subscribeToAgenda(() => {
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
      queryClient.invalidateQueries({ queryKey: ['pending-count'] });
    });
  }, [queryClient]);

  // Esconder é só não desenhar: o cancelamento continua no histórico e a falta
  // continua contando na ficha. O horário deles já estava livre na conta; o
  // cartão por cima é que impedia de tocar nele.
  const showInactive = session?.user.showInactiveAppointments ?? false;
  const inactive = agenda?.appointments.filter((item) => INACTIVE.has(item.status)) ?? [];
  const visibleAgenda =
    agenda && !showInactive
      ? {
          ...agenda,
          appointments: agenda.appointments.filter((item) => !INACTIVE.has(item.status)),
        }
      : agenda;

  const toggleInactive = useMutation({
    mutationFn: (show: boolean) => api.patch('/me', { showInactiveAppointments: show }),
    // Responde ao toque na hora; a sessão recarregada só confirma.
    onMutate: (show) => {
      queryClient.setQueryData<SessionContext>(['session'], (current) =>
        current
          ? { ...current, user: { ...current.user, showInactiveAppointments: show } }
          : current,
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['session'] }),
  });

  return (
    <div className="flex min-h-full flex-col">
      <ScreenHeader
        title={
          scope === 'dia'
            ? relativeDay(date, today, tenant.timezone)
            : scope === 'semana'
              ? 'Semana'
              : monthLabel(date)
        }
        subtitle={
          scope === 'dia'
            ? longDateOf(date, tenant.timezone)
            : weekOrMonthSubtitle(scope, date, today, tenant.timezone)
        }
        action={
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label={
                scope === 'mes'
                  ? 'Mês anterior'
                  : scope === 'semana'
                    ? 'Semana anterior'
                    : 'Dia anterior'
              }
              onClick={() => setDate(addDays(date, -step))}
              className="touch-target rounded-lg px-3 text-[18px] text-ink-soft active:bg-surface-raised"
            >
              ‹
            </button>

            {/*
              O controle do meio mostra a data em foco, não um rótulo fixo.
              Antes ele dizia "Hoje" sempre — ao lado das setas, isso se lê como
              indicador de posição, e mentia em todo dia que não fosse hoje.
              Agora ele abre o calendário do sistema: pular três semanas deixa
              de ser vinte toques na seta.
            */}
            <label className="touch-target relative flex items-center rounded-lg px-2 text-[13px] font-semibold tabular-nums text-ink-soft active:bg-surface-raised">
              {date.slice(8)}/{date.slice(5, 7)}
              <input
                type="date"
                value={date}
                aria-label="Escolher data"
                onChange={(event) => event.target.value && setDate(event.target.value)}
                className="absolute inset-0 opacity-0"
              />
            </label>

            <button
              type="button"
              aria-label={
                scope === 'mes'
                  ? 'Próximo mês'
                  : scope === 'semana'
                    ? 'Próxima semana'
                    : 'Próximo dia'
              }
              onClick={() => setDate(addDays(date, step))}
              className="touch-target rounded-lg px-3 text-[18px] text-ink-soft active:bg-surface-raised"
            >
              ›
            </button>
          </div>
        }
      />

      {/* Dia, semana ou mês: a mesma agenda em três distâncias */}
      <div className="flex items-center gap-2 border-b border-line px-4 py-2">
        <div className="flex flex-1 gap-1 rounded-lg bg-surface-sunken p-0.5">
          {(['dia', 'semana', 'mes'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setScope(option)}
              className={`flex-1 rounded-md py-1.5 text-[13px] font-medium capitalize ${
                scope === option ? 'bg-surface text-ink shadow-sm' : 'text-ink-soft'
              }`}
            >
              {option === 'mes' ? 'mês' : option}
            </button>
          ))}
        </div>

        {/* Só aparece quando há para onde voltar. */}
        {date !== today && (
          <button
            type="button"
            onClick={() => setDate(today)}
            className="touch-target shrink-0 rounded-lg px-3 text-[13px] font-semibold text-accent active:bg-surface-raised"
          >
            Hoje
          </button>
        )}
      </div>

      {scope === 'dia' && (
        <div className="scroll-touch flex gap-1.5 overflow-x-auto border-b border-line px-4 py-2">
          {Array.from({ length: 14 }, (_, index) => addDays(today, index - 2)).map((day) => (
            <button
              key={day}
              type="button"
              onClick={() => setDate(day)}
              className={`shrink-0 rounded-lg px-3 py-1.5 text-[13px] font-medium ${
                day === date ? 'bg-primary text-primary-ink' : 'bg-surface-raised text-ink-soft'
              }`}
            >
              {relativeDay(day, today, tenant.timezone)}
            </button>
          ))}
        </div>
      )}

      {isOwner && (staffData?.staff.length ?? 0) > 1 && (
        <div className="flex gap-1.5 border-b border-line px-4 py-2">
          {staffData?.staff.map((member) => {
            const active = (staffId ?? session?.membership?.id) === member.id;
            return (
              <button
                key={member.id}
                type="button"
                onClick={() => setStaffId(member.id)}
                className={`rounded-full px-3 py-1.5 text-[13px] font-medium ${
                  active
                    ? 'bg-accent text-accent-ink'
                    : 'border border-line bg-surface-raised text-ink-soft'
                }`}
              >
                {member.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 pt-4 pb-28">
        {scope !== 'dia' ? (
          <AgendaOverview
            scope={scope}
            date={date}
            today={today}
            membershipId={staffId ?? session?.membership?.id ?? null}
            timezone={tenant.timezone}
            // Tocar num dia leva para a linha do tempo dele — é lá que os
            // nomes estão.
            onPickDay={(picked) => {
              setDate(picked);
              setScope('dia');
            }}
          />
        ) : (
          <>
            {isPending && <Spinner />}

            {inactive.length > 0 && (
              <button
                type="button"
                aria-pressed={showInactive}
                onClick={() => toggleInactive.mutate(!showInactive)}
                className="touch-target mx-4 mb-3 flex w-[calc(100%-2rem)] items-center justify-between rounded-lg border border-line px-3 text-[13px] text-ink-soft active:bg-surface-raised"
              >
                <span>{inactiveLabel(inactive)}</span>
                <span className="font-semibold text-accent">
                  {showInactive ? 'Esconder' : 'Mostrar'}
                </span>
              </button>
            )}

            {visibleAgenda &&
              visibleAgenda.appointments.length === 0 &&
              visibleAgenda.workingRanges.length === 0 && (
                <EmptyState
                  title="Sem expediente neste dia"
                  description="Você pode marcar assim mesmo — o app só avisa que está fora do horário."
                />
              )}

            {visibleAgenda &&
              (visibleAgenda.appointments.length > 0 || visibleAgenda.workingRanges.length > 0) && (
                <Timeline
                  agenda={visibleAgenda}
                  today={today}
                  onSelect={setSelected}
                  onPickFreeTime={setCreatingAt}
                />
              )}
          </>
        )}
      </div>

      {/* Ação principal embaixo, no alcance do polegar */}
      {scope === 'dia' && (
        <div className="pb-safe pointer-events-none sticky bottom-0 z-40 px-4 pt-2">
          <Button full className="pointer-events-auto shadow-lg" onClick={() => setCreatingAt(-1)}>
            Novo agendamento
          </Button>
        </div>
      )}

      {selected && (
        <AppointmentSheet
          appointment={selected}
          timezone={tenant.timezone}
          onClose={() => setSelected(null)}
        />
      )}

      {creatingAt !== null && (
        <NewAppointmentSheet
          date={date}
          membershipId={staffId ?? session?.membership?.id ?? ''}
          initialMinutes={creatingAt >= 0 ? creatingAt : null}
          onClose={() => setCreatingAt(null)}
        />
      )}
    </div>
  );
}

/** "2 cancelados · 1 falta" — o recusado é contado junto com o cancelado. */
function inactiveLabel(appointments: AppointmentView[]): string {
  const noShows = appointments.filter((item) => item.status === 'NO_SHOW').length;
  const canceled = appointments.length - noShows;

  const parts = [
    canceled > 0 && `${canceled} ${canceled === 1 ? 'cancelado' : 'cancelados'}`,
    noShows > 0 && `${noShows} ${noShows === 1 ? 'falta' : 'faltas'}`,
  ].filter(Boolean);

  return parts.join(' · ');
}

/** "Agosto de 2026", com a inicial maiúscula. */
function monthLabel(date: string): string {
  const label = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));

  return label.charAt(0).toUpperCase() + label.slice(1);
}

function weekOrMonthSubtitle(
  scope: AgendaScope,
  date: string,
  today: string,
  timezone: string,
): string {
  if (scope === 'mes') return date.slice(0, 7) === today.slice(0, 7) ? 'Mês atual' : '';

  // A semana começa na segunda; domingo fecha a anterior.
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
  const monday = addDays(date, weekday === 0 ? -6 : 1 - weekday);
  const sunday = addDays(monday, 6);

  const curta = (value: string) =>
    new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      timeZone: timezone,
    }).format(new Date(`${value}T12:00:00Z`));

  return `${curta(monday)} a ${curta(sunday)}`;
}
